package com.iptvplayer.web.ui

import androidx.compose.runtime.Composable
import com.iptvplayer.shared.presentation.state.ContentIssue
import com.iptvplayer.shared.presentation.state.isNoSources
import com.iptvplayer.shared.presentation.state.message
import com.iptvplayer.shared.presentation.state.title
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun Field(
    label: String,
    value: String,
    placeholder: String = "",
    password: Boolean = false,
    onChange: (String) -> Unit,
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("margin-bottom", "14px")
        }
    }) {
        Label(attrs = {
            style {
                property("font-size", "13px")
                property("color", Theme.textDim)
                property("margin-bottom", "6px")
            }
        }) { Text(label) }

        val attrs: org.jetbrains.compose.web.attributes.builders.InputAttrsScope<String>.() -> Unit = {
            onInput { onChange(it.value) }
            if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
            style { fieldStyle() }
        }
        if (password) PasswordInput(value, attrs) else TextInput(value, attrs)
    }
}

@Composable
fun PrimaryButton(text: String, enabled: Boolean = true, fullWidth: Boolean = true, onClick: () -> Unit) {
    Button(attrs = {
        if (!enabled) disabled()
        onClick { onClick() }
        style {
            if (fullWidth) width(100.percent)
            property("background", if (enabled) Theme.primary else Theme.surfaceAlt)
            property("color", "#ffffff")
            property("border", "none")
            property("border-radius", "8px")
            property("padding", "11px 16px")
            property("font-size", "15px")
            property("font-weight", "600")
            property("cursor", if (enabled) "pointer" else "default")
        }
    }) { Text(text) }
}

@Composable
fun GhostButton(text: String, onClick: () -> Unit) {
    Button(attrs = {
        onClick { onClick() }
        style {
            property("background", "transparent")
            property("color", Theme.text)
            property("border", "1px solid ${Theme.border}")
            property("border-radius", "8px")
            property("padding", "8px 14px")
            property("font-size", "14px")
            property("cursor", "pointer")
        }
    }) { Text(text) }
}

@Composable
fun ErrorText(message: String?) {
    if (message != null) {
        Div(attrs = {
            style {
                property("background", "rgba(255,92,92,0.12)")
                property("border", "1px solid rgba(255,92,92,0.4)")
                property("color", Theme.danger)
                property("border-radius", "8px")
                property("padding", "10px 12px")
                property("font-size", "14px")
                property("margin-bottom", "14px")
            }
        }) { Text(message) }
    }
}

@Composable
fun IssueBanner(
    issue: ContentIssue,
    onRetry: () -> Unit,
    onOpenSources: (() -> Unit)? = null,
) {
    val noSources = issue.isNoSources
    Div(attrs = {
        style {
            property("background", if (noSources) "rgba(124,111,255,0.10)" else "rgba(255,92,92,0.10)")
            property("border", "1px solid " + (if (noSources) "rgba(124,111,255,0.45)" else "rgba(255,92,92,0.4)"))
            property("border-radius", "10px")
            property("padding", "14px 16px")
            property("margin-bottom", "16px")
        }
    }) {
        Div(attrs = {
            style {
                property("font-weight", "600"); property("font-size", "15px")
                property("color", if (noSources) Theme.primary else Theme.danger)
                property("margin-bottom", "6px")
            }
        }) { Text(issue.title) }
        Div(attrs = {
            style {
                property("color", Theme.textDim); property("font-size", "14px")
                property("white-space", "pre-line"); property("margin-bottom", "10px")
            }
        }) { Text(issue.message) }
        Div(attrs = { style { display(DisplayStyle.Flex); property("gap", "10px") } }) {
            if (noSources && onOpenSources != null) {
                SmallButton("Add a source", onOpenSources)
            } else {
                SmallButton("Retry", onRetry)
            }
        }
    }
}

@Composable
private fun SmallButton(text: String, onClick: () -> Unit) {
    Button(attrs = {
        onClick { onClick() }
        style {
            property("background", Theme.primary); property("color", "#fff")
            property("border", "none"); property("border-radius", "8px")
            property("padding", "7px 14px"); property("font-size", "14px")
            property("cursor", "pointer")
        }
    }) { Text(text) }
}

@Composable
fun Spinner(label: String = "Loading…") {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            flexDirection(FlexDirection.Column)
            property("gap", "14px")
            property("padding", "48px")
            property("color", Theme.textDim)
        }
    }) {
        Div(attrs = {
            style {
                property("width", "38px")
                property("height", "38px")
                property("border", "4px solid rgba(255,255,255,0.14)")
                property("border-top-color", Theme.primary)
                property("border-radius", "50%")
                property("animation", "iptvspin 1s linear infinite")
            }
        }) {}
        Span { Text(label) }
    }
    // keyframes are injected once via a <style> tag in App().
}
